import { useCallback, useEffect, useState } from "react";
import { useInvoiceApi } from "@/hooks/apiHooks/useInvoiceApi";
import type { GetMeInvoicesQuery, PaginatedMeInvoices } from "@models/invoice";

export type MeInvoicesState =
    | { status: "loading" }
    | { status: "failed" }
    | { status: "loaded"; page: PaginatedMeInvoices };

// The outcome of one request, tagged with the query and attempt it answered.
// `page: null` = that request failed.
interface Settled {
    query: GetMeInvoicesQuery;
    attempt: number;
    page: PaginatedMeInvoices | null;
}

// Fetches one page of GET /invoices/me for the Billing History (#215). The
// state is local and DERIVED: whatever settled for an older query or attempt
// reads as loading, so a sort, filter, page change or retry shows the spinner
// with no setState inside the effect, and a failure is never collapsed into
// the empty list. A response for a query the page has already moved past is
// discarded, so a slow early request cannot overwrite a later one.
//
// `query` must be memoized by the caller: a new object is a new request.
export const useMeInvoices = (query: GetMeInvoicesQuery) => {
    const { getMeInvoices } = useInvoiceApi();
    const [attempt, setAttempt] = useState(0);
    const [settled, setSettled] = useState<Settled | null>(null);

    useEffect(() => {
        let isCurrent = true;
        getMeInvoices(query).then(
            (page) => {
                if (isCurrent) setSettled({ query, attempt, page });
            },
            // useApi has already toasted; the failed state is the inline
            // record of it.
            () => {
                if (isCurrent) setSettled({ query, attempt, page: null });
            },
        );

        return () => {
            isCurrent = false;
        };
    }, [getMeInvoices, query, attempt]);

    const retry = useCallback(() => setAttempt((current) => current + 1), []);

    let state: MeInvoicesState;
    if (settled?.query !== query || settled.attempt !== attempt) {
        state = { status: "loading" };
    } else if (settled.page === null) {
        state = { status: "failed" };
    } else {
        state = { status: "loaded", page: settled.page };
    }

    return { state, retry };
};
